import { TicketPriority, TicketStatus, TicketType } from "./Ticket";

/** Mirrors TicketAiCategory on the API. Broader than TicketType on purpose. */
export enum TicketAiCategory {
  Bug,
  FeatureRequest,
  Question,
  Integration,
  Account,
  Billing,
  Other,
}

export enum TicketAiSentiment {
  Negative,
  Neutral,
  Positive,
}

export interface ITicketInsightQuery {
  customerId?: string;
  /** Tickets created in the last N days. 0 = all time. */
  days: number;
  includeResolved: boolean;
  similarityThreshold?: number;
}

export interface ITicketInsightStatus {
  isAiConfigured: boolean;
  totalInScope: number;
  analysed: number;
  pending: number;
  lastAnalysedAt?: string;
}

export interface ITicketInsightAnalyseRequest {
  customerId?: string;
  days: number;
  includeResolved: boolean;
  maxTickets?: number;
  force?: boolean;
}

export interface ITicketInsightAnalyseResult {
  analysed: number;
  failed: number;
  remaining: number;
  stopped: boolean;
  message?: string;
}

/** A ticket plus what the model made of it - the row behind every list on the page. */
export interface ITicketInsight {
  ticketId: string;
  subject: string;
  customerId: string;
  customerName?: string;
  assignedToUserId?: string;
  status: TicketStatus;
  priority: TicketPriority;
  type: TicketType;
  created: string;
  closedAt?: string;
  awaitingResponse: boolean;
  hasStaffReply: boolean;
  category: TicketAiCategory;
  categoryConfidence: number;
  topic: string;
  summary: string;
  sentiment: TicketAiSentiment;
  urgency: number;
  suggestedPriority: TicketPriority;
  tags: string[];
  analysedAt: string;
}

export interface ITicketInsightCategoryBreakdown {
  category: TicketAiCategory;
  count: number;
  active: number;
  done: number;
  percent: number;
  avgResolutionHours?: number;
  avgFirstReplyHours?: number;
}

export interface ITicketInsightTypeMismatch {
  ticket: ITicketInsight;
  suggestedType: TicketType;
}

export interface ITicketInsightTopic {
  topic: string;
  count: number;
  /** Same-length window before this one. Undefined for all-time scope. */
  previousCount?: number;
  active: number;
  customerCount: number;
  negativeCount: number;
  avgUrgency: number;
  avgResolutionHours?: number;
  dominantCategory: TicketAiCategory;
  categories: Record<string, number>;
  tickets: ITicketInsight[];
}

export interface ITicketInsightClusterMember {
  ticket: ITicketInsight;
  similarity: number;
}

export interface ITicketInsightCluster {
  id: number;
  label: string;
  count: number;
  active: number;
  customerCount: number;
  cohesion: number;
  likelyDuplicate: boolean;
  tickets: ITicketInsightClusterMember[];
}

export interface ITicketInsightSentiment {
  negative: number;
  neutral: number;
  positive: number;
  activeNegativePercent: number;
}

export interface ITicketInsightUrgencyBucket {
  urgency: number;
  count: number;
  active: number;
}

export interface ITicketInsightPriorityMismatch {
  ticket: ITicketInsight;
  gap: number;
}

export interface ITicketInsightEscalationRisk {
  ticket: ITicketInsight;
  score: number;
  reasons: string[];
}

export interface ITicketInsightWeek {
  weekStart: string;
  created: number;
  resolved: number;
  byCategory: Record<string, number>;
}

export interface ITicketInsightCustomer {
  customerId: string;
  customerName?: string;
  count: number;
  active: number;
  negativeCount: number;
  avgUrgency: number;
  topTopic: string;
  dominantCategory: TicketAiCategory;
}

export interface ITicketInsightKnowledgeGap {
  topic: string;
  count: number;
  customerCount: number;
  exampleSubjects: string[];
}

export interface ITicketInsightTag {
  tag: string;
  count: number;
}

export interface ITicketInsightAgeBucket {
  label: string;
  minDays: number;
  maxDays?: number;
  count: number;
  byCategory: Record<string, number>;
}

export interface ITicketInsightAnalytics {
  status: ITicketInsightStatus;
  generatedAt: string;
  similarityThreshold: number;
  categories: ITicketInsightCategoryBreakdown[];
  typeMismatches: ITicketInsightTypeMismatch[];
  topics: ITicketInsightTopic[];
  clusters: ITicketInsightCluster[];
  sentiment: ITicketInsightSentiment;
  urgency: ITicketInsightUrgencyBucket[];
  priorityMismatches: ITicketInsightPriorityMismatch[];
  escalationRisks: ITicketInsightEscalationRisk[];
  weeklyTrend: ITicketInsightWeek[];
  customers: ITicketInsightCustomer[];
  knowledgeGaps: ITicketInsightKnowledgeGap[];
  tags: ITicketInsightTag[];
  backlogAging: ITicketInsightAgeBucket[];
}
